import { useGeckoLocale } from 'features/locale'
import React from 'react'
import styled from 'styled-components'
import {
  GeckoColorScaleThreshold,
  GeckoMeasurement,
  GeckoMeasurementRange,
  GeckoMeasurementUnit,
} from 'types'

const Label = styled.label`
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 500;
  color: ${({ theme }) => theme.forecast.text1};
  min-width: 2rem;
`

const ColorCircle = styled.div<{ $color: string }>`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: ${(props) => props.$color};
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
`

const InputsGrid = styled.div`
  display: grid;
  gap: 4px;
  align-items: center;
  grid-template-columns: 1fr 1fr 1fr;
  justify-content: space-between;
  grid-template-areas:
    'min . max'
    't1 t2 t3';
`

const InputContainer = styled.div`
  display: flex;
  gap: 4px;
`

const InputContainerThresholds = styled.div`
  display: flex;
  height: 40px;
  align-items: center;
`

const UnitContainer = styled.h3`
  margin: auto;
  align-content: center;
  justify-content: center;
  font-weight: 500;
  height: 40px;
`

const Input = styled.input`
  display: block;
  width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  border-radius: 0.375rem;
  border-color: transparent;
  background-color: ${({ theme }) => theme.forecast.background1};
  color: ${({ theme }) => theme.forecast.defaultText};
  line-height: 1.5;

  &::placeholder {
    color: #9ca3af;
  }

  &:hover {
    background-color: ${({ theme }) => theme.forecast.hoverButton};
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px inset ${({ theme }) => theme.colors.highVis};
  }
`

export const RangeInputs: React.FC<{
  measurementUnit: GeckoMeasurementUnit
  fullValueRange: GeckoMeasurementRange
  filteredValueRange: GeckoMeasurementRange | undefined
  onFilteredValueRangeChange: (range: GeckoMeasurementRange | undefined) => void
  colorThreshold: GeckoColorScaleThreshold
  onColorThresholdChanged: (threshold: GeckoColorScaleThreshold) => void
  minThresholdColor: string
  midThresholdColor: string
  maxThresholdColor: string
}> = ({
  measurementUnit,
  fullValueRange,
  filteredValueRange = fullValueRange,
  onFilteredValueRangeChange,
  colorThreshold,
  onColorThresholdChanged,
  minThresholdColor,
  midThresholdColor,
  maxThresholdColor,
}) => {
  //----------------------------------------------------------------------------

  const { getPreferredMeasurementUnit } = useGeckoLocale()

  const { preferredUnit, scale, step } = React.useMemo(() => {
    const originalUnit = measurementUnit
    const preferredUnit = getPreferredMeasurementUnit(originalUnit)
    const scale = Math.ceil(
      Math.log10(fullValueRange.size.convertTo(preferredUnit).num)
    )
    const step = 0.001 * Math.pow(10.0, scale)
    return { preferredUnit, fullValueRange, scale, step }
  }, [getPreferredMeasurementUnit, measurementUnit, fullValueRange])

  //----------------------------------------------------------------------------

  const parseStringInput = React.useCallback(
    (value: string | undefined, forceWithinRange: boolean) => {
      // If there's no string input, return undefined
      if (value === '' || value === undefined) return undefined

      // The minus sign can stand in for 0
      const num = value === '-' ? -0 : Number(value)
      if (Number.isNaN(num)) return undefined

      if (forceWithinRange) {
        const min = fullValueRange.min.convertTo(preferredUnit).num
        const max = fullValueRange.max.convertTo(preferredUnit).num
        return GeckoMeasurement.CreateFromValue(
          Math.min(Math.max(num, min), max),
          preferredUnit
        )
      }
      return GeckoMeasurement.CreateFromValue(num, preferredUnit)
    },
    [fullValueRange, preferredUnit]
  )

  const formatMeasurement = React.useCallback(
    (measurement: GeckoMeasurement, forceWithinRange: boolean) => {
      const min = fullValueRange.min.convertTo(preferredUnit).num
      const max = fullValueRange.max.convertTo(preferredUnit).num
      const num = measurement.convertTo(preferredUnit).num
      const decimalPlaces = Math.min(Math.max(3 - scale, 0), 3)
      if (forceWithinRange) {
        return Math.min(Math.max(min, num), max).toFixed(decimalPlaces)
      }
      return num.toFixed(decimalPlaces)
    },
    [fullValueRange, scale, preferredUnit]
  )

  //----------------------------------------------------------------------------

  const [minFilterString, setMinFilterString] = React.useState<string>()
  const [maxFilterString, setMaxFilterString] = React.useState<string>()

  React.useEffect(() => {
    const min =
      parseStringInput(minFilterString, true) ?? filteredValueRange.min
    const max =
      parseStringInput(maxFilterString, true) ?? filteredValueRange.max
    onFilteredValueRangeChange(
      GeckoMeasurementRange.CreateFromStartEnd(min, max)
    )
  }, [
    maxFilterString,
    minFilterString,
    parseStringInput,
    filteredValueRange.min,
    filteredValueRange.max,
    onFilteredValueRangeChange,
  ])

  const [minCTString, setMinCTString] = React.useState<string>()
  const [midCTString, setMidCTString] = React.useState<string>()
  const [maxCTString, setMaxCTString] = React.useState<string>()

  const updateColorScaleThresholds = React.useCallback(
    (passive?: boolean) => {
      const min = parseStringInput(minCTString, false) ?? colorThreshold.min
      const mid = parseStringInput(midCTString, false) ?? colorThreshold.mid
      const max = parseStringInput(maxCTString, false) ?? colorThreshold.max

      // If we're passively updating the color scale (via typing in color scale
      // values without pressing "Enter") ONLY update the color scale threshold
      // if min < mid < max... otherwise, we'll get some strange visual behavior
      if (passive && (min.num >= mid.num || mid.num >= max.num)) return

      const newColorThreshold = GeckoColorScaleThreshold.CreateFromMeasurements(
        min,
        mid,
        max
      )
      if (!GeckoColorScaleThreshold.Equals(newColorThreshold, colorThreshold))
        onColorThresholdChanged(newColorThreshold)

      if (!passive) {
        setMinCTString(undefined)
        setMidCTString(undefined)
        setMaxCTString(undefined)
      }
    },
    [
      minCTString,
      midCTString,
      maxCTString,
      colorThreshold,
      parseStringInput,
      onColorThresholdChanged,
    ]
  )

  React.useEffect(() => {
    updateColorScaleThresholds(true)
  }, [minCTString, midCTString, maxCTString, updateColorScaleThresholds])

  React.useEffect(() => {
    // Every time the preferred measurement unit changes, reset the inputs
    setMinFilterString(undefined)
    setMaxFilterString(undefined)
    setMinCTString(undefined)
    setMidCTString(undefined)
    setMaxCTString(undefined)
  }, [preferredUnit])

  //----------------------------------------------------------------------------

  return (
    <InputContainer>
      <InputsGrid>
        <InputContainer
          style={{ gridArea: 'min', height: '40px', alignItems: 'center' }}>
          <Label htmlFor='min-filter'>Min</Label>
          <Input
            id='min-filter'
            type='number'
            step={step}
            value={
              minFilterString ?? formatMeasurement(filteredValueRange.min, true)
            }
            onChange={(e) => setMinFilterString(e.currentTarget.value)}
            onBlur={() => setMinFilterString(undefined)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.currentTarget.blur()
            }}
          />
        </InputContainer>
        <UnitContainer>
          {preferredUnit !== GeckoMeasurementUnit.None
            ? preferredUnit.name
            : null}
        </UnitContainer>
        <InputContainer
          style={{ gridArea: 'max', height: '40px', alignItems: 'center' }}>
          <Label htmlFor='max-filter'>Max</Label>
          <Input
            id='max-filter'
            type='number'
            step={step}
            value={
              maxFilterString ?? formatMeasurement(filteredValueRange.max, true)
            }
            onChange={(e) => setMaxFilterString(e.currentTarget.value)}
            onBlur={() => setMaxFilterString(undefined)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.currentTarget.blur()
            }}
          />
        </InputContainer>
        <InputContainerThresholds style={{ gridArea: 't1' }}>
          <Label htmlFor='min-threshhold'>
            <ColorCircle $color={minThresholdColor} />
          </Label>
          <Input
            id='min-threshhold'
            type='number'
            step={step}
            value={minCTString ?? formatMeasurement(colorThreshold.min, false)}
            onChange={(e) => setMinCTString(e.currentTarget.value)}
            onBlur={() => updateColorScaleThresholds()}
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.currentTarget.blur()
            }}
          />
        </InputContainerThresholds>
        <InputContainerThresholds style={{ gridArea: 't2' }}>
          <Label htmlFor='mid-threshhold'>
            <ColorCircle $color={midThresholdColor} />
          </Label>
          <Input
            id='mid-threshhold'
            type='number'
            step={step}
            value={midCTString ?? formatMeasurement(colorThreshold.mid, false)}
            onChange={(e) => setMidCTString(e.currentTarget.value)}
            onBlur={() => updateColorScaleThresholds()}
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.currentTarget.blur()
            }}
          />
        </InputContainerThresholds>
        <InputContainerThresholds style={{ gridArea: 't3' }}>
          <Label htmlFor='max-threshhold'>
            <ColorCircle $color={maxThresholdColor} />
          </Label>
          <Input
            id='max-threshhold'
            type='number'
            step={step}
            value={maxCTString ?? formatMeasurement(colorThreshold.max, false)}
            onChange={(e) => setMaxCTString(e.currentTarget.value)}
            onBlur={() => updateColorScaleThresholds()}
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.currentTarget.blur()
            }}
          />
        </InputContainerThresholds>
      </InputsGrid>
    </InputContainer>
  )
}
